import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Navigation2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { CIRCUITS } from "../lib/circuits";
import { WA_LINK } from "../lib/content";

export default function Activities() {
  const { t } = useI18n();

  return (
    <>
      <section className="pt-16 md:pt-24 pb-12 md:pb-16" data-testid="activities-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">{t("activities.overline")}</div>
          <h1 className="mt-5 text-5xl md:text-7xl leading-[1.02] max-w-4xl">
            {t("activities.title")}
          </h1>
          <p className="mt-6 text-lg text-[#5C5042] max-w-2xl">
            {t("activities.subtitle")}
          </p>
          <p className="mt-3 text-sm text-[#5C5042]/80 italic">
            {t("activities.source")}
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 space-y-20 md:space-y-28">
          {CIRCUITS.map((circuit, i) => (
            <CircuitCard key={circuit.id} circuit={circuit} index={i} />
          ))}
        </div>
      </section>

      <ActivitiesCta />
    </>
  );
}

function CircuitCard({ circuit, index }) {
  const { t, lang } = useI18n();
  const isEven = index % 2 === 0;
  const fr = lang === "fr";
  const title = fr ? circuit.titleFr : circuit.titleEn;
  const stops = fr ? circuit.stopsFr : circuit.stopsEn;
  const note = fr ? circuit.noteFr : circuit.noteEn;

  return (
    <article
      id={circuit.id}
      data-testid={`circuit-${circuit.id}`}
      className="grid md:grid-cols-12 gap-8 md:gap-12 items-start scroll-mt-28"
    >
      <div
        className={`md:col-span-5 relative md:sticky md:top-28 ${
          isEven ? "" : "md:order-2"
        }`}
      >
        <div className="bg-[#EFEAE1] p-3 md:p-4 shadow-[0_12px_40px_-12px_rgba(44,36,27,0.25)]">
          <div className="relative w-full aspect-[3/4] bg-[#F9F6F0] overflow-hidden">
            <img
              src={circuit.image}
              alt={title}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>

      <div className={`md:col-span-7 ${isEven ? "" : "md:order-1"}`}>
        <div className="flex items-baseline gap-4">
          <span className="font-serif-display text-5xl md:text-6xl text-[#6B4A2B]/60">
            {circuit.number}
          </span>
          <span className="overline">
            {fr ? circuit.overlineFr : circuit.overlineEn}
          </span>
        </div>
        <h2 className="mt-4 text-3xl md:text-5xl leading-[1.05]">{title}</h2>

        <p className="mt-6 text-lg text-[#5C5042] leading-relaxed">
          {fr ? circuit.introFr : circuit.introEn}
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 border-t border-[#6B4A2B]/15 pt-6">
          <CircuitMeta
            icon={Clock}
            label={t("activities.duration")}
            value={fr ? circuit.durationFr : circuit.durationEn}
          />
          <CircuitMeta
            icon={Navigation2}
            label={t("activities.means")}
            value={fr ? circuit.meansFr : circuit.meansEn}
          />
        </div>

        <div className="mt-8">
          <div className="overline mb-4">{t("activities.stops")}</div>
          <ol className="space-y-3">
            {stops.map((s, idx) => (
              <li key={`${circuit.id}-stop-${idx}`} className="flex gap-4 text-[#2C241B]">
                <span className="overline text-[#6B4A2B] min-w-[24px]">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="text-[15px] leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        {note && (
          <p className="mt-6 text-sm italic text-[#5C5042] border-l-2 border-[#4F7942] pl-4">
            {note}
          </p>
        )}

        <div className="mt-8 flex gap-3 flex-wrap">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid={`circuit-book-${circuit.id}`}
            className="btn-primary"
          >
            {t("activities.book")} <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </article>
  );
}

function CircuitMeta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} strokeWidth={1.3} className="mt-1 text-[#4F7942] shrink-0" />
      <div>
        <div className="overline text-[#5C5042]">{label}</div>
        <div className="mt-1 text-sm text-[#2C241B]">{value}</div>
      </div>
    </div>
  );
}

function ActivitiesCta() {
  const { t } = useI18n();

  return (
    <section className="py-20 bg-[#2C241B] text-[#F9F6F0]" data-testid="activities-cta">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
        <div className="overline text-[#F9F6F0]/70">
          {t("activities.cta_overline")}
        </div>
        <h2 className="mt-4 text-4xl md:text-5xl leading-[1.1]">
          {t("activities.cta_title")}
        </h2>
        <p className="mt-6 text-[#F9F6F0]/80 text-lg">{t("activities.cta_body")}</p>
        <div className="mt-10 flex gap-4 justify-center flex-wrap">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
            style={{ background: "#25D366" }}
            data-testid="activities-cta-whatsapp"
          >
            WhatsApp <ArrowUpRight size={14} />
          </a>
          <Link
            to="/reserver"
            className="btn-outline"
            style={{ borderColor: "#F9F6F0", color: "#F9F6F0" }}
            data-testid="activities-cta-book"
          >
            {t("nav.book")}
          </Link>
        </div>
        <p className="mt-8 text-xs text-[#F9F6F0]/50 italic">
          {t("activities.office")}
        </p>
      </div>
    </section>
  );
}
